import React, { useEffect, useRef, useState } from 'react';
import { resolveAssetUrl } from '../lib/assets';

type ModelViewerElement = HTMLElement & {
    activateAR?: () => Promise<void> | void;
    canActivateAR?: boolean;
};

type ProductModelViewerProps = {
    src?: string | null;
    iosSrc?: string | null;
    poster?: string | null;
    alt: string;
    className?: string;
};

const ProductModelViewer: React.FC<ProductModelViewerProps> = ({ src, iosSrc, poster, alt, className }) => {
    const viewerRef = useRef<ModelViewerElement | null>(null);
    const [viewerState, setViewerState] = useState<'loading' | 'loaded' | 'error'>('loading');
    const [displayMode, setDisplayMode] = useState<'photo' | 'model'>('model');
    const [isActivatingAr, setIsActivatingAr] = useState(false);
    const [arMessage, setArMessage] = useState('');
    const resolvedPoster = poster ? resolveAssetUrl(poster) : null;
    const resolvedIosSrc = iosSrc ? resolveAssetUrl(iosSrc) : null;

    useEffect(() => {
        if (!src) {
            setViewerState('error');
            return;
        }

        const viewer = viewerRef.current;
        setViewerState('loading');
        setArMessage('');

        if (!viewer) {
            return;
        }

        const handleLoad = () => {
            setViewerState('loaded');
        };

        const handleError = () => {
            setViewerState('error');
        };

        viewer.addEventListener('load', handleLoad as EventListener);
        viewer.addEventListener('error', handleError as EventListener);

        return () => {
            viewer.removeEventListener('load', handleLoad as EventListener);
            viewer.removeEventListener('error', handleError as EventListener);
        };
    }, [src]);

    useEffect(() => {
        setDisplayMode('model');
    }, [src]);

    if (!src) {
        return null;
    }

    const showingPhoto = displayMode === 'photo' && !!resolvedPoster;
    const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : '';
    const isIPhone = /iPhone|iPad|iPod/i.test(userAgent);
    const isAndroid = /Android/i.test(userAgent);
    const arModes = isAndroid ? 'scene-viewer' : 'webxr scene-viewer quick-look';
    const canAttemptAr = viewerState === 'loaded' && !showingPhoto && (!isIPhone || !!resolvedIosSrc);

    const handleActivateAr = async () => {
        if (isIPhone && !resolvedIosSrc) {
            setArMessage('AR nativo no iPhone exige um arquivo USDZ, e este produto ainda nao tem esse formato cadastrado.');
            return;
        }

        try {
            const viewer = viewerRef.current;
            if (!viewer?.activateAR) {
                setArMessage('AR nao disponivel neste aparelho ou navegador.');
                return;
            }

            setArMessage('');
            setIsActivatingAr(true);
            await viewer.activateAR();
        } catch (error) {
            console.error('Failed to activate AR', error);
            setArMessage('Nao foi possivel abrir o modo AR neste aparelho.');
        } finally {
            setIsActivatingAr(false);
        }
    };

    return (
        <div className={className}>
            {resolvedPoster && (
                <div className="mb-3 flex gap-2">
                    <button
                        type="button"
                        onClick={() => setDisplayMode('photo')}
                        className={`rounded-full border px-3 py-1.5 text-[11px] font-semibold transition ${
                            showingPhoto
                                ? 'border-white/20 bg-white/10 text-white'
                                : 'border-primary/20 bg-primary/10 text-primary hover:border-primary/40'
                        }`}
                    >
                        Ver foto
                    </button>
                    <button
                        type="button"
                        onClick={() => setDisplayMode('model')}
                        className={`rounded-full border px-3 py-1.5 text-[11px] font-semibold transition ${
                            !showingPhoto
                                ? 'border-white/20 bg-white/10 text-white'
                                : 'border-primary/20 bg-primary/10 text-primary hover:border-primary/40'
                        }`}
                    >
                        Ver 3D
                    </button>
                </div>
            )}
            <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-[#050505]">
                {React.createElement('model-viewer', {
                    ref: viewerRef,
                    src: resolveAssetUrl(src),
                    'ios-src': resolvedIosSrc || undefined,
                    alt,
                    ar: true,
                    'ar-modes': arModes,
                    'ar-scale': 'auto',
                    'ar-placement': 'floor',
                    'camera-controls': true,
                    'auto-rotate': true,
                    'shadow-intensity': '1',
                    exposure: '1',
                    loading: 'eager',
                    reveal: 'auto',
                    'interaction-prompt': 'auto',
                    'touch-action': 'pan-y',
                    className: `aspect-[4/3] h-auto w-full bg-[#050505] transition-opacity duration-300 ${
                        viewerState === 'loaded' && !showingPhoto ? 'opacity-100' : 'pointer-events-none opacity-0'
                    }`,
                })}

                {(viewerState !== 'loaded' || showingPhoto) && (
                    <div className="absolute inset-0 overflow-hidden rounded-2xl bg-[#121212]">
                        {resolvedPoster ? (
                            <img
                                src={resolvedPoster}
                                alt={alt}
                                className="h-full w-full object-cover"
                            />
                        ) : (
                            <div className="flex h-full w-full items-center justify-center px-6 text-center text-sm text-muted-foreground">
                                Visualizacao 3D indisponivel neste aparelho.
                            </div>
                        )}
                    </div>
                )}

                {viewerState !== 'loaded' && !showingPhoto && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-[#0b0b0b]/70 px-6 text-center">
                        {viewerState === 'loading' ? (
                            <>
                                <div className="h-8 w-8 animate-spin rounded-full border-2 border-white/20 border-t-primary" />
                                <div className="space-y-1">
                                    <p className="text-sm font-semibold text-white">Carregando modelo 3D...</p>
                                    <p className="text-xs text-muted-foreground">
                                        O produto so sera exibido quando o carregamento terminar.
                                    </p>
                                </div>
                            </>
                        ) : (
                            <div className="space-y-2">
                                <p className="text-sm font-semibold text-amber-300">Falha ao carregar o modelo 3D.</p>
                                <p className="text-xs text-muted-foreground">
                                    Tente novamente em instantes ou use outro aparelho/navegador.
                                </p>
                            </div>
                        )}
                    </div>
                )}
            </div>

            <div className="mt-3 space-y-3 text-xs text-muted-foreground">
                <p>
                    {showingPhoto
                        ? 'Visualizando a foto original do produto.'
                        : viewerState === 'loaded'
                        ? resolvedIosSrc
                            ? 'Arraste para girar o prato em 3D ou abra em AR no seu aparelho.'
                            : 'Arraste para girar o prato em 3D.'
                        : viewerState === 'loading'
                            ? 'Aguarde o modelo carregar completamente para liberar a visualizacao 3D.'
                            : 'O modelo nao ficou disponivel neste carregamento.'}
                </p>
                <div className="flex flex-wrap gap-2">
                    <span
                        className={`rounded-full border px-2.5 py-1 ${
                            showingPhoto
                                ? 'border-white/10 bg-white/5 text-white/70'
                                : viewerState === 'loaded'
                                ? 'border-primary/20 bg-primary/10 text-primary'
                                : viewerState === 'loading'
                                    ? 'border-white/10 bg-white/5 text-white/70'
                                    : 'border-amber-500/20 bg-amber-500/10 text-amber-300'
                        }`}
                    >
                        {showingPhoto ? 'Foto ativa' : viewerState === 'loaded' ? '3D ativo' : viewerState === 'loading' ? 'Carregando 3D' : '3D indisponivel'}
                    </span>
                    <span>
                        {showingPhoto
                            ? 'Toque em "Ver 3D" para voltar ao modelo.'
                            : viewerState === 'loaded'
                            ? resolvedIosSrc
                                ? 'Visualizacao 3D pronta. AR disponivel em aparelhos compativeis.'
                                : 'Visualizacao 3D pronta.'
                            : viewerState === 'loading'
                                ? 'Preparando visualizacao do modelo.'
                                : 'Visualizacao indisponivel neste carregamento.'}
                    </span>
                </div>
                {viewerState === 'loaded' && !showingPhoto && (
                    <button
                        type="button"
                        onClick={handleActivateAr}
                        disabled={!canAttemptAr || isActivatingAr}
                        className="inline-flex items-center justify-center rounded-full border border-primary/30 bg-primary px-4 py-2 text-xs font-semibold text-black shadow-lg transition hover:brightness-105 disabled:cursor-not-allowed disabled:border-white/10 disabled:bg-white/10 disabled:text-white/55"
                    >
                        {isActivatingAr ? 'Carregando...' : 'Ver em AR'}
                    </button>
                )}
                {arMessage && (
                    <p className="text-[11px] text-amber-300">{arMessage}</p>
                )}
            </div>
        </div>
    );
};

export default ProductModelViewer;
